import axiosInstance from '@/services/axios.service';
import { getPointsName } from '@/services/points.service';
import { getPersonalsName } from '@/services/personal.service';
import {
  buildReferenceMaps,
  normalizeEmployee,
  normalizeEmployeeCheckRecord,
  normalizeOrderItemsPayload,
  normalizePoint,
} from '@/views/statistic/employeeAnalytics/normalizers';
import {
  buildEmployeeRatingRows,
  buildEmployeeSummary,
  buildTopCheck,
  buildTopDish,
} from '@/views/statistic/employeeAnalytics/calculators';
import { normalizePersonalOrdersSummary } from '@/views/statistic/employeeAnalytics/summaryAdapter';
import { getOrderAmounts } from '@/utils/orderAmounts';

const ORDER_SORT_FIELDS = ['createdAt', 'resultSum', 'rating', 'tableId', 'number'];
const ORDER_SORT_DIRECTIONS = ['asc', 'desc'];
const SUMMARY_FALLBACK_PAGE_SIZE = 200;
const SUMMARY_FALLBACK_MAX_RECORDS = 5000;
const EMPLOYEE_AGGREGATE_PAGE_SIZE = 200;
const EMPLOYEE_AGGREGATE_MAX_RECORDS = 5000;

function normalizeOrderSortField(value, fallback = 'createdAt') {
  return ORDER_SORT_FIELDS.includes(value) ? value : fallback;
}

function normalizeOrderSortDirection(value, fallback = 'desc') {
  return ORDER_SORT_DIRECTIONS.includes(value) ? value : fallback;
}

function toUnixSeconds(value) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) return 0;
  return normalized > 9999999999 ? Math.floor(normalized / 1000) : Math.floor(normalized);
}

function buildDateRangePayload(dateRange = []) {
  if (!Array.isArray(dateRange) || dateRange.length !== 2) return {};

  const dateFrom = toUnixSeconds(dateRange[0]);
  const dateTo = toUnixSeconds(dateRange[1]);
  if (!dateFrom || !dateTo || dateFrom > dateTo) return {};

  return { dateFrom, dateTo };
}

function extractResponseTotal(response = {}, fallback = 0) {
  const total =
    response?.total ?? response?.data?.total ?? response?.data?.meta?.total ?? response?.data?.pagination?.total;
  const normalized = Number(total);
  if (!Number.isFinite(normalized) || normalized < 0) return fallback;
  return normalized;
}

function buildOrdersSummaryPayload(payload = {}) {
  const statuses = Array.isArray(payload?.statuses)
    ? payload.statuses.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  return {
    ...(payload?.pointId ? { pointId: payload.pointId } : {}),
    dateFrom: Number(payload?.dateFrom || 0),
    dateTo: Number(payload?.dateTo || 0),
    ...(statuses.length ? { statuses } : {}),
  };
}

function buildOrdersSummarySearchPayload({
  pointId = '',
  dateFrom = 0,
  dateTo = 0,
  statuses = ['Paid'],
  take = SUMMARY_FALLBACK_PAGE_SIZE,
  skip = 0,
} = {}) {
  const normalizedStatuses = Array.isArray(statuses)
    ? statuses.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  return {
    pagination: {
      take: Number(take) || SUMMARY_FALLBACK_PAGE_SIZE,
      skip: Number(skip) || 0,
    },
    filters: {
      ...(pointId ? { pointId: String(pointId) } : {}),
      ...(normalizedStatuses.length ? { status: normalizedStatuses } : {}),
    },
    fields: [
      'id',
      'pointId',
      'personalId',
      'employeeId',
      'personalName',
      'employeeName',
      'tableId',
      'number',
      'fullSum',
      'resultSum',
      'rating',
      'createdAt',
      'updatedAt',
      'status',
      'serviceType',
      'type',
      'bonusWriteOffAmount',
      'appliedBonus',
      'writeOffBonus',
      'loyaltyBonus',
    ],
    sort: 'createdAt',
    orderBy: 'desc',
    ...(dateFrom ? { dateFrom: Number(dateFrom) } : {}),
    ...(dateTo ? { dateTo: Number(dateTo) } : {}),
  };
}

function isNotFoundSummaryResponse(response = {}) {
  return (
    response?.success === false && response?.cause?.code === 'NOT_FOUND' && Number(response?.cause?.status) === 404
  );
}

function extractResponseRows(response = {}) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.orders)) return response.orders;
  if (Array.isArray(response?.data?.orders)) return response.data.orders;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  return [];
}

function normalizePointIdsInput(pointId = '', pointIds = []) {
  const payloadPointIds = Array.isArray(pointIds)
    ? pointIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  if (payloadPointIds.length) return payloadPointIds;

  const normalizedPointId = String(pointId || '').trim();
  return normalizedPointId ? [normalizedPointId] : [];
}

async function getOrdersForSummaryFallback(payload = {}) {
  const unique = new Map();
  let skip = 0;
  let total = 0;

  while (unique.size < SUMMARY_FALLBACK_MAX_RECORDS) {
    const response = await axiosInstance.post(
      '/operator/v1/orders/search',
      buildOrdersSummarySearchPayload({
        ...payload,
        take: SUMMARY_FALLBACK_PAGE_SIZE,
        skip,
      }),
    );
    const rows = extractResponseRows(response);
    total = extractResponseTotal(response, total || rows.length);

    for (const row of rows) {
      const key = String(row?.id ?? row?.orderId ?? row?.checkId ?? `${skip}:${unique.size}`);
      unique.set(key, row);
      if (unique.size >= SUMMARY_FALLBACK_MAX_RECORDS) break;
    }

    skip += SUMMARY_FALLBACK_PAGE_SIZE;
    if (!rows.length || rows.length < SUMMARY_FALLBACK_PAGE_SIZE) break;
    if (total && skip >= total) break;
  }

  if (unique.size >= SUMMARY_FALLBACK_MAX_RECORDS) {
    console.warn('getPersonalOrdersSummary:fallback-limit-reached', {
      limit: SUMMARY_FALLBACK_MAX_RECORDS,
      pointId: String(payload?.pointId || ''),
    });
  }

  return {
    orders: [...unique.values()],
    total: total || unique.size,
  };
}

async function getPersonalOrdersSummaryFallback(payload = {}, refs = {}) {
  const { orders } = await getOrdersForSummaryFallback(payload);
  const records = orders.map((order) =>
    normalizeEmployeeCheckRecord(
      {
        ...order,
        status: order?.status ?? 'Paid',
        serviceType: order?.serviceType ?? 'dine-in',
      },
      refs,
    ),
  );
  const rows = buildEmployeeRatingRows(records).map((row, index) => ({
    rank: index + 1,
    ...row,
  }));

  return {
    success: true,
    data: {
      summary: normalizePersonalOrdersSummary(buildEmployeeSummary(records)),
      rows,
      total: rows.length,
    },
    meta: {
      source: 'orders-search-fallback',
    },
  };
}

function buildEmployeeOrdersSearchPayload({
  personalId = '',
  pointId = '',
  take = 20,
  skip = 0,
  sort = 'createdAt',
  orderBy = 'desc',
  dateRange = [],
  includeItems = false,
  includePagination = true,
  orderIds = [],
} = {}) {
  const normalizedSort = normalizeOrderSortField(sort);
  const normalizedOrderIds = Array.isArray(orderIds)
    ? orderIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const fields = [
    'id',
    'pointId',
    'tableId',
    'number',
    'fullSum',
    'resultSum',
    'rating',
    'createdAt',
    'notes',
  ];

  if (includeItems) {
    fields.push('orderItems', 'discounts');
  }

  return {
    ...(includePagination
      ? {
          pagination: {
            take: Number(take) || 20,
            skip: Number(skip) || 0,
          },
        }
      : {}),
    filters: {
      ...(pointId ? { pointId: String(pointId) } : {}),
      ...(normalizedOrderIds.length ? { orderIds: normalizedOrderIds } : {}),
      personalId: [String(personalId)],
      // completed: true,
      status: ['Paid'],
    },
    fields,
    sort: normalizedSort,
    orderBy: normalizeOrderSortDirection(orderBy, normalizedSort === 'tableId' ? 'asc' : 'desc'),
    ...buildDateRangePayload(dateRange),
  };
}

function buildEmployeeTopDishSearchPayload({ personalId = '', pointIds = [], dateRange = [], take = 0, skip = 0 } = {}) {
  const normalizedPointIds = Array.isArray(pointIds)
    ? pointIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  return {
    pagination: {
      take: Number(take) || EMPLOYEE_AGGREGATE_PAGE_SIZE,
      skip: Number(skip) || 0,
    },
    filters: {
      ...(normalizedPointIds.length === 1 ? { pointId: normalizedPointIds[0] } : {}),
      personalId: [String(personalId)],
      status: ['Paid'],
    },
    fields: ['id', 'pointId', 'orderItems', 'discounts'],
    ...buildDateRangePayload(dateRange),
  };
}

function buildEmployeeTopCheckSearchPayload({ personalId = '', pointIds = [], dateRange = [], take = 0, skip = 0 } = {}) {
  const normalizedPointIds = Array.isArray(pointIds)
    ? pointIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  return {
    pagination: {
      take: Number(take) || EMPLOYEE_AGGREGATE_PAGE_SIZE,
      skip: Number(skip) || 0,
    },
    filters: {
      ...(normalizedPointIds.length === 1 ? { pointId: normalizedPointIds[0] } : {}),
      personalId: [String(personalId)],
      status: ['Paid'],
    },
    fields: ['id', 'pointId', 'resultSum', 'fullSum'],
    ...buildDateRangePayload(dateRange),
  };
}

async function getOrdersForEmployeeAggregate({ payloadBuilder, personalId = '', pointIds = [], dateRange = [] } = {}) {
  const unique = new Map();
  let skip = 0;
  let total = 0;

  while (unique.size < EMPLOYEE_AGGREGATE_MAX_RECORDS) {
    const response = await axiosInstance.post(
      '/operator/v1/orders/search',
      payloadBuilder({
        personalId,
        pointIds,
        dateRange,
        take: EMPLOYEE_AGGREGATE_PAGE_SIZE,
        skip,
      }),
    );
    const rows = extractResponseRows(response);
    total = extractResponseTotal(response, total || rows.length);

    for (const row of rows) {
      const key = String(row?.id ?? row?.orderId ?? row?.checkId ?? `${skip}:${unique.size}`);
      unique.set(key, row);
      if (unique.size >= EMPLOYEE_AGGREGATE_MAX_RECORDS) break;
    }

    skip += EMPLOYEE_AGGREGATE_PAGE_SIZE;
    if (!rows.length || rows.length < EMPLOYEE_AGGREGATE_PAGE_SIZE) break;
    if (total && skip >= total) break;
  }

  if (unique.size >= EMPLOYEE_AGGREGATE_MAX_RECORDS) {
    console.warn('getOrdersForEmployeeAggregate:limit-reached', {
      personalId: String(personalId || ''),
      pointIds,
      limit: EMPLOYEE_AGGREGATE_MAX_RECORDS,
    });
  }

  return {
    orders: [...unique.values()],
    total: total || unique.size,
  };
}

async function getOrdersByEmployee({
  personalId = '',
  pointId = '',
  take = 20,
  skip = 0,
  maxRecords = 5000,
  sort = 'createdAt',
  orderBy = 'desc',
  dateRange = [],
  loadAll = false,
  includeItems = false,
  orderIds = [],
} = {}) {
  const normalizedId = String(personalId || '');
  if (!normalizedId) return { orders: [], total: 0 };

  const pageSize = Math.max(1, Math.min(Number(take) || 20, 200));
  const limit = Math.max(pageSize, Number(maxRecords) || 0);
  const unique = new Map();
  let nextSkip = Math.max(0, Number(skip) || 0);
  let total = NaN;
  let hasMorePages = true;

  while (hasMorePages) {
    const payload = buildEmployeeOrdersSearchPayload({
      personalId: normalizedId,
      pointId,
      take: pageSize,
      skip: nextSkip,
      sort,
      orderBy,
      dateRange,
      includeItems,
      includePagination: true,
      orderIds,
    });

    const response = await axiosInstance.post('/operator/v1/orders/search', payload);
    const rows = extractResponseRows(response);
    const parsedTotal = extractResponseTotal(response, NaN);
    if (Number.isFinite(parsedTotal)) total = parsedTotal;

    for (const row of rows) {
      const key = String(row?.id ?? row?.orderId ?? row?.checkId ?? '');
      if (key) unique.set(key, row);
      if (unique.size >= limit) break;
    }

    if (!loadAll || !rows.length || unique.size >= limit) {
      hasMorePages = false;
      continue;
    }

    nextSkip += rows.length;
    hasMorePages =
      (Number.isFinite(total) && nextSkip < total) ||
      (!Number.isFinite(total) && rows.length >= pageSize);
  }

  if (unique.size >= limit) {
    console.warn('getOrdersByEmployee:limit-reached', {
      personalId: normalizedId,
      pointId: String(pointId || ''),
      limit,
    });
  }

  return {
    orders: [...unique.values()],
    total: Number.isFinite(total) ? total : unique.size,
  };
}

export async function getEmployeeOrderItems({ personalId = '', orderId = '', pointId = '', dateRange = [] } = {}) {
  const normalizedPersonalId = String(personalId || '');
  const normalizedOrderId = String(orderId || '');
  if (!normalizedPersonalId || !normalizedOrderId) return [];

  const payload = {
    pagination: {
      take: 1,
      skip: 0,
    },
    filters: {
      ...(pointId ? { pointId: String(pointId) } : {}),
      personalId: [normalizedPersonalId],
      orderIds: [normalizedOrderId],
    },
    fields: ['orderItems', 'discounts'],
    sort: 'tableId',
    orderBy: 'asc',
    ...buildDateRangePayload(dateRange),
  };

  const response = await axiosInstance.post('/operator/v1/orders/search', payload);
  if (process.env.NODE_ENV !== 'production') {
    console.log('employeeAnalytics:getEmployeeOrderItems:payload', payload);
    console.log('employeeAnalytics:getEmployeeOrderItems:response', response);
  }

  const order = response?.data?.orders?.[0] || {};
  const items = order?.orderItems;
  const discounts = order?.discounts;
  return {
    orderItems: Array.isArray(items) ? items : [],
    discounts: Array.isArray(discounts) ? discounts : [],
  };
}

export async function getEmployeeTopDish({ employeeId = '', pointId = '', pointIds = [], dateRange = [] } = {}) {
  const normalizedId = String(employeeId || '');
  if (!normalizedId) {
    return {
      topDish: buildTopDish([]),
      total: 0,
    };
  }
  const normalizedPointIds = normalizePointIdsInput(pointId, pointIds);
  const { orders, total } = await getOrdersForEmployeeAggregate({
    payloadBuilder: buildEmployeeTopDishSearchPayload,
    personalId: normalizedId,
    pointIds: normalizedPointIds,
    dateRange,
  });
  const scopedOrders =
    normalizedPointIds.length > 1
      ? orders.filter((order) => normalizedPointIds.includes(String(order?.pointId || '')))
      : orders;
  const records = scopedOrders.map((order) => ({
    items: normalizeOrderItemsPayload({
      orderItems: order?.orderItems,
      discounts: order?.discounts,
    }),
  }));

  return {
    topDish: buildTopDish(records),
    total,
  };
}

export async function getEmployeeTopCheck({ employeeId = '', pointId = '', pointIds = [], dateRange = [] } = {}) {
  const normalizedId = String(employeeId || '');
  if (!normalizedId) {
    return {
      topCheck: null,
      total: 0,
    };
  }
  const normalizedPointIds = normalizePointIdsInput(pointId, pointIds);
  const { orders, total } = await getOrdersForEmployeeAggregate({
    payloadBuilder: buildEmployeeTopCheckSearchPayload,
    personalId: normalizedId,
    pointIds: normalizedPointIds,
    dateRange,
  });
  const scopedOrders =
    normalizedPointIds.length > 1
      ? orders.filter((order) => normalizedPointIds.includes(String(order?.pointId || '')))
      : orders;
  const records = scopedOrders.map((order) => ({
    checkId: String(order?.id ?? order?.orderId ?? order?.checkId ?? ''),
    netAmount: getOrderAmounts(order).net,
  }));

  return {
    topCheck: buildTopCheck(records),
    total,
  };
}

export async function getEmployeeAnalyticsReferenceData() {
  const [points, employees] = await Promise.all([getPointsName(), getPersonalsName()]);
  const normalizedPoints = Array.isArray(points) ? points.map((point) => normalizePoint(point)) : [];
  const normalizedEmployees = Array.isArray(employees) ? employees.map((employee) => normalizeEmployee(employee)) : [];

  return {
    points: normalizedPoints,
    employees: normalizedEmployees,
    ...buildReferenceMaps({
      points: normalizedPoints,
      employees: normalizedEmployees,
    }),
  };
}

export async function getEmployeeChecksFeed({
  employeeId,
  dateRange = [],
  pointId = '',
  refs: initialRefs = null,
  sort = 'createdAt',
  orderBy = 'desc',
  page = 1,
  rowsPerPage = 10,
  loadAll = false,
  orderIds = [],
} = {}) {
  const refs = initialRefs || (await getEmployeeAnalyticsReferenceData());
  const take = loadAll ? EMPLOYEE_AGGREGATE_PAGE_SIZE : Math.max(1, Math.min(Number(rowsPerPage) || 10, 200));
  const normalizedPage = Math.max(1, Number(page) || 1);
  const { orders, total } = await getOrdersByEmployee({
    personalId: employeeId,
    pointId,
    take,
    skip: loadAll ? 0 : (normalizedPage - 1) * take,
    sort,
    orderBy,
    dateRange,
    loadAll,
    includeItems: loadAll,
    maxRecords: loadAll ? Number.MAX_SAFE_INTEGER : undefined,
    orderIds,
  });

  const records = orders.map((order) =>
    normalizeEmployeeCheckRecord(
      {
        ...order,
        employeeId,
        status: order?.status ?? 'Paid',
        serviceType: order?.serviceType ?? 'dine-in',
        personalId: employeeId,
        pointId: pointId || order?.pointId,
      },
      refs,
    ),
  );

  return {
    records,
    total,
    refs,
  };
}

/**
 * @typedef {Object} PersonalOrdersSummaryRequest
 * @property {string} [region='eu'] Region code.
 * @property {string} merchantId Merchant identifier.
 * @property {string} [pointId=''] Point of sale identifier. Empty string means all points.
 * @property {number} dateFrom Start of the requested interval as a Unix timestamp.
 * @property {number} dateTo End of the requested interval as a Unix timestamp.
 */

/**
 * @typedef {Object} PersonalOrdersSummaryMetric
 * @property {number} value Primary metric value.
 * @property {'up'|'down'|'flat'} [trendDirection] Trend direction versus the comparison period.
 * @property {number} [trendPercent] Relative trend in percents.
 */

/**
 * @typedef {Object} PersonalOrdersSummaryRow
 * @property {number} rank Position in the rating table.
 * @property {string} personalId Employee identifier.
 * @property {string} personalName Employee full name.
 * @property {string|null} [pointId] Point of sale identifier.
 * @property {string} pointTitle Point of sale title.
 * @property {number} revenue Employee revenue for the interval.
 * @property {number} revenueShare Share of total revenue in percents.
 * @property {number} checksCount Number of paid checks.
 * @property {number} averageCheck Average check amount.
 * @property {number} bonuses Bonus write-off amount.
 * @property {number} bonusShare Share of total bonuses in percents.
 * @property {number|null} guestRating Average guest rating.
 */

/**
 * @typedef {Object} PersonalOrdersSummaryData
 * @property {Object} summary KPI cards for the selected interval.
 * @property {PersonalOrdersSummaryMetric} summary.revenue
 * @property {PersonalOrdersSummaryMetric} summary.checksCount
 * @property {PersonalOrdersSummaryMetric} summary.averageCheck
 * @property {PersonalOrdersSummaryMetric} summary.bonuses
 * @property {PersonalOrdersSummaryMetric} summary.guestRating
 * @property {PersonalOrdersSummaryRow[]} rows Staff rating rows.
 * @property {number} total Total number of rows before UI slicing or pagination.
 */

/**
 * @typedef {Object} PersonalOrdersSummaryResponse
 * @property {boolean} success
 * @property {PersonalOrdersSummaryData|Array} data
 * @property {string} [message]
 */

/**
 * Request body:
 * {
 *   region: 'eu',
 *   merchantId: 'IFbvgbLgcoTCD5JnxP0a',
 *   pointId: 'VGhCrGEX3KNTPU9SqqcR',
 *   dateFrom: 1744675200,
 *   dateTo: 1745366399
 * }
 *
 * Expected response shape for the employee rating screen:
 * {
 *   success: true,
 *   data: {
 *     summary: {
 *       revenue: { value: 3220936, trendDirection: 'up', trendPercent: 10 },
 *       checksCount: { value: 1654, trendDirection: 'up', trendPercent: 3 },
 *       averageCheck: { value: 2160, trendDirection: 'up', trendPercent: 12 },
 *       bonuses: { value: 282078, trendDirection: 'down', trendPercent: 9 },
 *       guestRating: { value: 4.0, trendDirection: 'up', trendPercent: 10 }
 *     },
 *     rows: [
 *       {
 *         rank: 1,
 *         personalId: 'personal_1',
 *         personalName: 'Смирнов Алексей',
 *         pointId: 'point_1',
 *         pointTitle: 'Frank by Basta на Мясницкой',
 *         revenue: 619920,
 *         revenueShare: 19.3,
 *         checksCount: 246,
 *         averageCheck: 2520,
 *         bonuses: 48420,
 *         bonusShare: 1.5,
 *         guestRating: 4.5
 *       }
 *     ],
 *     total: 9
 *   }
 * }
 *
 * @param {PersonalOrdersSummaryRequest} payload
 * @returns {Promise<PersonalOrdersSummaryResponse>}
 */
export async function getPersonalOrdersSummary(payload = {}) {
  const body = buildOrdersSummaryPayload(payload);
  const refs = payload?.refs || {};

  try {
    const response = await axiosInstance.post('/operator/v1/statistic/orders-summary', body);
    if (isNotFoundSummaryResponse(response)) {
      return getPersonalOrdersSummaryFallback(body, refs);
    }
    return response;
  } catch (error) {
    console.error('getPersonalOrdersSummary:error', error);
    return getPersonalOrdersSummaryFallback(body, refs);
  }
}
